import './utils/highlight' // Highlight
import 'simplebar/src/simplebar.css' // Scrollbar
import 'react-image-lightbox/style.css' // Lightbox
import 'react-quill/dist/quill.snow.css' // Rich Text Editors
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css' // Rich Text Editors
import 'slick-carousel/slick/slick.css' // Carousel
import 'slick-carousel/slick/slick-theme.css' // Carousel
import '_style.css' // Global Styles

import React, {useEffect, useState} from 'react'
import ReactDOM from 'react-dom'
import {HelmetProvider, Helmet} from 'react-helmet-async'
import {BrowserRouter} from 'react-router-dom'
import {SettingsProvider} from 'utils/context/settings'
import {CollapseDrawerProvider} from 'utils/context/drawer'
import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import reportWebVitals from './reportWebVitals'
import storage from 'utils/storage'
import {useTawkWidget} from 'utils/hooks/useTawkWidget'
import networkMonitor from 'utils/performance/networkMonitor'

const USERSNAP_GLOBAL_API_KEY = '70ccdab5-9822-416e-b752-ef1d60667f77'
const LOADING_TIMEOUT = 30000

const AppWithTawk = () => {
  const [formattedUser, setFormattedUser] = useState(null)
  const [isIOS, setIsIOS] = useState(/iPhone|iPad|iPod/i.test(navigator.userAgent))
  const [loadingState, setLoadingState] = useState({
    isLoading: true,
    error: null,
    retryCount: 0,
  })

  // Use the optimized Tawk.to hook
  const {
    isLoading: tawkLoading,
    isLoaded: tawkLoaded,
    error: tawkError,
    retryCount: tawkRetryCount,
    performanceMetrics: tawkMetrics,
  } = useTawkWidget(formattedUser, {
    lazyLoad: true, // Enable lazy loading
    backgroundLoad: true, // Enable background loading
    enableMonitoring: true, // Enable performance monitoring
    onLoad: () => {
      console.log('Tawk.to widget loaded successfully in background')
    },
    onError: (error) => {
      console.warn('Tawk.to widget failed to load:', error)
    },
    onTimeout: () => {
      console.warn('Tawk.to widget loading timed out')
    },
  })

  // Handle loader visibility
  useEffect(() => {
    if (loadingState.error) {
      // Show error in loader
      const loaderText = document.getElementById('root-loader-text')
      if (loaderText) {
        loaderText.innerHTML = `Error: ${loadingState.error}<br/><button id="retry-button" style="margin-top:10px;padding:5px 15px;border-radius:4px;background:#1976d2;color:white;border:none;cursor:pointer;">Retry</button>`
        document.getElementById('retry-button')?.addEventListener('click', () => {
          setLoadingState((prev) => ({
            isLoading: true,
            error: null,
            retryCount: prev.retryCount + 1,
          }))
        })
      }
    } else if (loadingState.isLoading) {
      window.showRootLoader?.('Loading application...')
    } else {
      // Hide loader when loading completes successfully
      window.hideRootLoader?.()
    }
  }, [loadingState])

  // Main initialization with timeout and error handling
  useEffect(() => {
    if (!loadingState.isLoading) return

    let isMounted = true
    let timeoutId = null

    // Set timeout to prevent indefinite loading
    timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoadingState({
          isLoading: false,
          error: 'Loading timed out. Please check your connection and try again.',
          retryCount: loadingState.retryCount,
        })
      }
    }, LOADING_TIMEOUT)

    const initialize = async () => {
      try {
        setIsIOS(/iPhone|iPad|iPod/i.test(navigator.userAgent))

        // Handle iOS-specific reload with better error handling and prevention of infinite loops
        if (isIOS) {
          try {
            // Check if we can access sessionStorage (fails in private browsing mode)
            const canUseSessionStorage = (() => {
              try {
                const testKey = '__storage_test__'
                sessionStorage.setItem(testKey, 'test')
                sessionStorage.removeItem(testKey)
                return true
              } catch (e) {
                return false
              }
            })()

            if (canUseSessionStorage) {
              const iosReloaded = sessionStorage.getItem('iosReloaded')
              const reloadAttempts = parseInt(sessionStorage.getItem('iosReloadAttempts') || '0')

              // Prevent infinite reload loops by limiting attempts
              if (!iosReloaded && reloadAttempts < 2) {
                sessionStorage.setItem('iosReloaded', 'true')
                sessionStorage.setItem('iosReloadAttempts', (reloadAttempts + 1).toString())
                window.location.reload()
                return
              }

              // Clear reload attempts after successful load
              if (iosReloaded) {
                sessionStorage.removeItem('iosReloadAttempts')
              }
            } else {
              // If sessionStorage doesn't work (private browsing), use localStorage as fallback
              try {
                const iosReloaded = localStorage.getItem('iosReloaded')
                const reloadAttempts = parseInt(localStorage.getItem('iosReloadAttempts') || '0')

                if (!iosReloaded && reloadAttempts < 2) {
                  localStorage.setItem('iosReloaded', 'true')
                  localStorage.setItem('iosReloadAttempts', (reloadAttempts + 1).toString())
                  // Add a small delay before reload to prevent rapid reload loops
                  setTimeout(() => {
                    window.location.reload()
                  }, 100)
                  return
                }

                if (iosReloaded) {
                  localStorage.removeItem('iosReloadAttempts')
                }
              } catch (localStorageError) {
                console.warn(
                  'Both sessionStorage and localStorage failed on iOS, continuing without reload:',
                  localStorageError,
                )
                // Continue without reload if both storage methods fail
              }
            }
          } catch (storageError) {
            console.warn('iOS reload handling failed, continuing with app initialization:', storageError)
            // Continue with app initialization even if reload logic fails
          }
        }

        // Fetch user data
        const token = await storage.getToken()
        const user = await storage.getUser()

        if (user && isMounted) {
          const userObj = JSON.parse(user)
          setFormattedUser(userObj)

          // Tawk.to widget is now handled by the useTawkWidget hook
          // No need to manually load it here - it will load in the background
        }

        // Mark loading as complete if we reach here without errors
        if (isMounted) {
          setLoadingState({
            isLoading: false,
            error: null,
            retryCount: loadingState.retryCount,
          })
        }
      } catch (error) {
        console.error('Initialization error:', error)
        if (isMounted) {
          setLoadingState({
            isLoading: false,
            error: error.message || 'Failed to initialize application',
            retryCount: loadingState.retryCount,
          })
        }
      }
    }

    initialize()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [isIOS, loadingState.isLoading, loadingState.retryCount])

  // Log performance metrics in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && tawkLoaded) {
      console.log('Tawk.to Performance Metrics:', tawkMetrics)
      console.log('Network Monitor Metrics:', networkMonitor.getMetrics())
      console.log('Tawk.to Specific Metrics:', networkMonitor.getTawkToMetrics())
    }
  }, [tawkLoaded, tawkMetrics])

  // Render fallback for critical errors
  if (loadingState.error && loadingState.retryCount > 3) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <h2>Unable to load application</h2>
        <p>{loadingState.error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          Reload Application
        </button>
      </div>
    )
  }

  return (
    <HelmetProvider>
      <Helmet>
        <title>My App</title>
      </Helmet>
      <SettingsProvider>
        <CollapseDrawerProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </CollapseDrawerProvider>
      </SettingsProvider>
    </HelmetProvider>
  )
}

// Global functions to control root loader
window.showRootLoader = (message = 'Loading application...') => {
  const rootLoader = document.getElementById('root-loader')
  const loaderStatus = document.getElementById('root-loader-status')
  if (rootLoader) {
    rootLoader.style.display = 'flex'
    rootLoader.style.opacity = '1'
  }
  if (loaderStatus) {
    loaderStatus.innerHTML = message
    loaderStatus.classList.add('blink')
  }
}

window.hideRootLoader = () => {
  const rootLoader = document.getElementById('root-loader')
  if (rootLoader) {
    rootLoader.style.opacity = '0'
    setTimeout(() => {
      rootLoader.style.display = 'none'
    }, 300)
  }
}

// Initialize the root loader visibility
document.addEventListener('DOMContentLoaded', () => {
  const rootLoader = document.getElementById('root-loader')
  if (rootLoader) {
    rootLoader.style.display = 'flex'
  }
})

ReactDOM.render(
  <React.StrictMode>
    <AppWithTawk />
  </React.StrictMode>,
  document.getElementById('root'),
)

// If you want to enable client cache, register instead.
serviceWorkerRegistration.unregister()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
reportWebVitals()
